import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, ChevronRight, Loader2, SkipForward, Users } from 'lucide-react';
import { apiService } from '../../services/api';
import ProjectReview, { IterationInput } from './ProjectReview';
import { ago, notesFor, queueOf } from './review';

// Revue d'avancement : le titulaire de la roadmap passe ses projets un par un (ceux à revoir d'abord), voit ce qui
// s'est passé depuis le dernier point et ce qui reste, et note le % de chaque itération en cours (ou « rien n'a
// changé »). Lancée depuis l'accueil, le menu, ou le message de Nexus avant une tactique. ?projet=ID ouvre un projet.

const tacticalLabel = (tactical) => {
  if (!tactical) return null;
  const start = new Date(tactical.start);
  const today = new Date();
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const day = sameDay(start, today) ? "aujourd'hui" : sameDay(start, tomorrow) ? 'demain'
    : start.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const time = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${tactical.title} ${day} ${tactical.source === 'habit' ? 'vers' : 'à'} ${time}`;
};

const Stepper = ({ projects, queue, current, results, onPick }) => (
  <nav className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" aria-label="Projets de la revue">
    {projects.map((p) => {
      const result = Array.isArray(results[p.id]) ? results[p.id] : null; // passé : reste à revoir
      const active = p.id === current;
      const inQueue = queue.includes(p.id);
      return (
        <button key={p.id} type="button" onClick={() => onPick(p.id)}
          className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${active
            ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm'
            : result ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : inQueue ? 'border-slate-300 bg-white text-slate-700 hover:border-indigo-300'
                : 'border-slate-200 bg-white/60 text-slate-400 hover:text-slate-600'}`}
          title={!p.iterations?.length ? 'Pas d’itération en cours' : inQueue ? 'À revoir' : `Vu ${ago(p.seen_at) || ''}`}>
          {result && !active && <Check size={12} />}
          <span className="max-w-[11rem] truncate">{p.name}</span>
        </button>
      );
    })}
  </nav>
);

const Done = ({ projects, results, onHome, onBack }) => {
  const reviewed = projects.filter((p) => results[p.id]);
  const skipped = reviewed.filter((p) => results[p.id] === 'skipped').length;
  return (
    <div className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
      <CheckCircle2 size={36} className="mx-auto text-emerald-500" />
      <h2 className="mt-2 text-lg font-semibold text-slate-900">Revue terminée</h2>
      <p className="mt-1 text-sm text-slate-500">
        {skipped
          ? `${skipped} projet${skipped > 1 ? 's' : ''} passé${skipped > 1 ? 's' : ''} : ${skipped > 1 ? 'ils restent' : 'il reste'} à revoir.`
          : 'L’avancement est à jour pour la tactique.'}
      </p>
      {reviewed.length > 0 && (
        <ul className="mt-5 divide-y divide-slate-100 text-left">
          {reviewed.map((p) => (
            <li key={p.id} className="flex items-baseline justify-between gap-3 py-2 text-sm">
              <span className="min-w-0 truncate text-slate-700">{p.name}</span>
              <span className="shrink-0 text-xs tabular-nums text-slate-500">
                {results[p.id] === 'skipped' ? 'passé'
                  : results[p.id].map((item) => (item.changed
                    ? `${item.previous == null ? '—' : `${Math.round(item.previous)} %`} → ${Math.round(item.percent)} %`
                    : `inchangé (${Math.round(item.percent)} %)`)).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-6 flex justify-center gap-2">
        <button type="button" onClick={onBack} className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Revoir un projet</button>
        <button type="button" onClick={onHome} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700">Retour à l’accueil</button>
      </div>
    </div>
  );
};

const ProgressReviewPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [dossiers, setDossiers] = useState({});
  const [drafts, setDrafts] = useState({});
  const [results, setResults] = useState({});
  const [saving, setSaving] = useState(false);
  const [finished, setFinished] = useState(false);
  const fetching = useRef(new Set());

  useEffect(() => {
    apiService.get('/progress-review').then((data) => {
      setState(data);
      const order = queueOf(data.projects);
      setQueue(order);
      const asked = Number(params.get('projet'));
      setCurrent(data.projects.some((p) => p.id === asked) ? asked : order[0] ?? null);
    }).catch((err) => setError(err.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDossier = useCallback(async (projectId, force = false) => {
    if (!projectId || (!force && fetching.current.has(projectId))) return;
    fetching.current.add(projectId);
    try {
      const data = await apiService.get(`/progress-review/projects/${projectId}`);
      setDossiers((prev) => ({ ...prev, [projectId]: data }));
    } catch (err) {
      setDossiers((prev) => ({ ...prev, [projectId]: { error: err.message } }));
    }
  }, []);

  // Le projet affiché, et le suivant de la file en avance pour enchaîner sans attendre.
  const next = useMemo(() => {
    const pending = queue.filter((id) => id !== current && !results[id]);
    const after = queue.slice(queue.indexOf(current) + 1).find((id) => !results[id]);
    return after ?? pending[0] ?? null;
  }, [queue, current, results]);
  useEffect(() => { fetchDossier(current); }, [current, fetchDossier]);
  useEffect(() => { if (dossiers[current]) fetchDossier(next); }, [dossiers, current, next, fetchDossier]);
  useEffect(() => {
    if (current && Number(params.get('projet')) !== current) setParams({ projet: String(current) }, { replace: true });
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (projectId) => {
    setFinished(false);
    setError(null);
    setCurrent(projectId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const advance = (updated) => {
    const target = queue.slice(queue.indexOf(current) + 1).find((id) => !updated[id])
      ?? queue.find((id) => id !== current && !updated[id]);
    if (target) go(target);
    else setFinished(true);
  };

  const dossier = dossiers[current];
  const iterations = dossier?.iterations || [];
  const projectDrafts = drafts[current] || {};
  const notes = notesFor(iterations, projectDrafts);
  const readOnly = dossier?.roadmap?.can === false;

  const save = async () => {
    if (!notes.ready || saving) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await apiService.post(`/progress-review/projects/${current}`, { iterations: notes.entries });
      const updated = { ...results, [current]: saved.items };
      setResults(updated);
      setDrafts((prev) => ({ ...prev, [current]: {} }));
      setState((prev) => ({ ...prev, projects: prev.projects.map((p) => (p.id === current ? { ...p, due: false, seen_at: saved.seen.at } : p)) }));
      fetchDossier(current, true);
      advance(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const skip = () => {
    const updated = { ...results, [current]: results[current] || 'skipped' };
    setResults(updated);
    advance(updated);
  };

  // Entrée (hors champ de commentaire) : enregistrer et passer au suivant.
  const onKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.tagName === 'INPUT' && event.target.inputMode === 'decimal') {
      event.preventDefault();
      save();
    }
  };

  if (!state) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-16 text-center text-sm text-slate-500">
        {error || <Loader2 size={18} className="mx-auto animate-spin text-slate-400" />}
      </div>
    );
  }

  const reviewedCount = queue.filter((id) => Array.isArray(results[id])).length;
  const tactical = tacticalLabel(state.next_tactical);
  const panel = dossier && !dossier.error ? (
    <div className="space-y-3" onKeyDown={onKeyDown} role="presentation">
      {iterations.length ? iterations.map((it, index) => (
        <IterationInput key={it.id} iteration={it} readOnly={readOnly} autoFocus={index === 0 && !readOnly}
          draft={projectDrafts[it.id] || {}} objectives={dossier.remaining.by_iteration?.[it.id] || { done: 0, total: 0 }}
          onChange={(draft) => setDrafts((prev) => ({ ...prev, [current]: { ...(prev[current] || {}), [it.id]: draft } }))} />
      )) : (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 p-4 text-sm text-slate-500">
          Pas d’itération datée en cours sur ce projet : rien à noter. La roadmap se règle depuis la fiche du projet.
        </div>
      )}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="flex items-center gap-2">
        <button type="button" onClick={skip} className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:bg-white hover:text-slate-800">
          <SkipForward size={14} /> Passer
        </button>
        {iterations.length > 0 && !readOnly && (
          <button type="button" onClick={save} disabled={!notes.ready || saving}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-40">
            {saving ? <Loader2 size={14} className="animate-spin" /> : notes.changed ? <Check size={14} /> : <ArrowRight size={14} />}
            {notes.changed || !notes.ready ? 'Enregistrer' : 'Rien n’a changé'}
            {next ? ' · suivant' : ''}
          </button>
        )}
      </div>
      {iterations.length > 0 && !readOnly && !notes.ready && (
        <p className="text-right text-[11px] text-slate-400">Donne un % à chaque itération.</p>
      )}
    </div>
  ) : null;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-3 py-4 sm:px-4 sm:py-6">
      <header className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/')} title="Accueil" className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-slate-700">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Revue d’avancement</h1>
              <p className="text-xs text-slate-500">
                {state.projects.length
                  ? `${reviewedCount} / ${queue.length} ${queue.length > 1 ? 'projets' : 'projet'} · les projets dont tu tiens la roadmap`
                  : 'Les projets dont tu tiens la roadmap'}
              </p>
            </div>
          </div>
          {tactical && (
            <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs text-slate-600 ring-1 ring-slate-200">
              <Users size={12} className="text-indigo-500" /> {tactical}
            </span>
          )}
        </div>
        {state.projects.length > 1 && (
          <Stepper projects={state.projects} queue={queue} current={finished ? null : current} results={results} onPick={go} />
        )}
      </header>

      {!state.projects.length ? (
        <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600 shadow-sm">
          Tu ne tiens la roadmap d’aucun projet en cours. La revue d’avancement est faite par le titulaire du domaine
          « Roadmap » de chaque projet (rôle Leader de projet et son focus).
        </div>
      ) : finished ? (
        <Done projects={state.projects} results={results} onHome={() => navigate('/')}
          onBack={() => go(queue[0] ?? state.projects[0].id)} />
      ) : !dossier ? (
        <p className="flex items-center gap-2 py-10 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Chargement du projet…</p>
      ) : dossier.error ? (
        <p className="py-10 text-sm text-rose-600">{dossier.error}</p>
      ) : (
        <ProjectReview data={dossier} panel={panel} onOpenProject={() => navigate(`/project/${current}`)} />
      )}

      {!finished && next && dossier && !dossier.error && (
        <button type="button" onClick={() => go(next)}
          className="flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-700">
          Ensuite : {state.projects.find((p) => p.id === next)?.name} <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
};

export default ProgressReviewPage;
